// ProvenanceTab — DAG view rooted at the campaign node.
//
//   Campaign
//     ├── Batch 1 ── SamplePoint …
//     ├── Batch 2 ── SamplePoint …
//     └── (Surrogate, when status === 'done')
//
// Fork edges (dashed) connect a source batch to each forked child campaign.
// Manual-source sample points get an "M" badge (see SamplePointNode).

import { useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import type { CampaignState, SamplePoint } from '../types';
import { useCampaignStateQuery } from '../api';
import CampaignNode from './CampaignNode';
import BatchNode from './BatchNode';
import SamplePointNode from './SamplePointNode';
import SurrogateNode from './SurrogateNode';
import ForkNode from './ForkNode';
import { layoutProvenance } from './provenanceLayout';
import { fallbackTheme } from './theme';

const NODE_TYPES = {
  campaign: CampaignNode,
  batch: BatchNode,
  point: SamplePointNode,
  surrogate: SurrogateNode,
  fork: ForkNode,
};

const PRO_OPTIONS = { hideAttribution: true };

interface Props {
  campaignId: string;
}

interface ForkEdgeInfo {
  fromId: string;
  toId: string;
  atIteration: number;
}

// Optional fork endpoint (W2 may or may not expose this). We dynamic-import
// from '../api' so that a missing export just yields `undefined` instead of a
// module-level crash.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FetchCampaignForks = (id: string) => Promise<ForkEdgeInfo[]>;

async function tryFetchCampaignForks(id: string): Promise<ForkEdgeInfo[]> {
  try {
    // TODO: wire fork edges once W2 exposes fetchCampaignForks.
    const mod = (await import('../api')) as unknown as {
      fetchCampaignForks?: FetchCampaignForks;
    };
    if (typeof mod.fetchCampaignForks === 'function') {
      return await mod.fetchCampaignForks(id);
    }
  } catch {
    // api module missing or throws — treat as "no fork edges available".
  }
  return [];
}

function groupByBatch(points: SamplePoint[]): Map<string, SamplePoint[]> {
  const m = new Map<string, SamplePoint[]>();
  for (const p of points) {
    const list = m.get(p.batchId);
    if (list) list.push(p);
    else m.set(p.batchId, [p]);
  }
  return m;
}

function summariseConfig(state: CampaignState): string {
  const inputNames = state.config.inputs.map((i) => i.name).join(' × ');
  const outputNames = state.config.outputs.map((o) => o.name).join(', ');
  return `${inputNames || 'inputs'} → ${outputNames || 'outputs'} · budget ${state.config.budget} · batch ${state.config.batchSize}`;
}

function finalMaxSigma(state: CampaignState): number | undefined {
  const values = Object.values(state.metrics.perOutput).map((m) => m.currentMaxSigma);
  if (values.length === 0) return undefined;
  return Math.max(...values);
}

function buildGraph(
  state: CampaignState,
  forks: ForkEdgeInfo[],
): { nodes: Node[]; edges: Edge[] } {
  const t = fallbackTheme;
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const campaignNodeId = `campaign:${state.id}`;
  nodes.push({
    id: campaignNodeId,
    type: 'campaign',
    position: { x: 0, y: 0 },
    data: {
      name: state.config.name,
      createdAt: state.createdAt,
      configSummary: summariseConfig(state),
    },
  });

  const batchMap = groupByBatch(state.points);
  // Stable ordering by (iteration asc, batchId lexicographic).
  const orderedBatches = [...batchMap.entries()].sort(([aId, aPts], [bId, bPts]) => {
    const ai = aPts[0]?.iter ?? 0;
    const bi = bPts[0]?.iter ?? 0;
    if (ai !== bi) return ai - bi;
    return aId.localeCompare(bId);
  });

  for (const [batchId, batchPoints] of orderedBatches) {
    const iter = batchPoints[0]?.iter ?? 0;
    const batchNodeId = `batch:${batchId}`;
    const doneCount = batchPoints.filter((p) => p.status === 'done').length;
    const failedCount = batchPoints.filter((p) => p.status === 'failed').length;

    nodes.push({
      id: batchNodeId,
      type: 'batch',
      position: { x: 0, y: 0 },
      data: {
        iteration: iter,
        pointCount: batchPoints.length,
        doneCount,
        failedCount,
      },
    });

    edges.push({
      id: `e:campaign-${batchId}`,
      source: campaignNodeId,
      target: batchNodeId,
      style: { stroke: t.batchEdge, strokeWidth: 1.5 },
    });

    for (const p of batchPoints) {
      const pointNodeId = `point:${p.id}`;
      nodes.push({
        id: pointNodeId,
        type: 'point',
        position: { x: 0, y: 0 },
        data: { point: p },
      });
      edges.push({
        id: `e:${batchId}-${p.id}`,
        source: batchNodeId,
        target: pointNodeId,
        style: { stroke: t.batchEdge, strokeWidth: 1 },
      });
    }
  }

  if (state.status === 'done') {
    const surrogateNodeId = `surrogate:${state.id}`;
    nodes.push({
      id: surrogateNodeId,
      type: 'surrogate',
      position: { x: 0, y: 0 },
      data: {
        pointsComplete: state.pointsComplete,
        finalMaxSigma: finalMaxSigma(state),
      },
    });
    // Connect surrogate from the last batch node (if any) or campaign.
    const lastBatch = orderedBatches[orderedBatches.length - 1];
    const sourceId = lastBatch ? `batch:${lastBatch[0]}` : campaignNodeId;
    edges.push({
      id: `e:${sourceId}-surrogate`,
      source: sourceId,
      target: surrogateNodeId,
      style: { stroke: t.accent, strokeWidth: 1.5 },
    });
  }

  // Fork edges: dashed from source batch node to a "fork" node that links to
  // the forked child campaign. The source batch is picked by matching the
  // `atIteration` to the first batch with that iter value.
  const batchByIter = new Map<number, string>();
  for (const [batchId, pts] of orderedBatches) {
    const iter = pts[0]?.iter ?? 0;
    if (!batchByIter.has(iter)) batchByIter.set(iter, batchId);
  }

  for (const fork of forks) {
    if (fork.fromId !== state.id) continue;
    const sourceBatchId = batchByIter.get(fork.atIteration);
    const sourceNodeId = sourceBatchId
      ? `batch:${sourceBatchId}`
      : campaignNodeId;
    const forkNodeId = `fork:${fork.toId}`;
    nodes.push({
      id: forkNodeId,
      type: 'fork',
      position: { x: 0, y: 0 },
      data: {
        campaignId: fork.toId,
        atIteration: fork.atIteration,
      },
    });
    edges.push({
      id: `e:fork-${sourceNodeId}-${fork.toId}`,
      source: sourceNodeId,
      target: forkNodeId,
      animated: true,
      style: { stroke: t.forkEdge, strokeWidth: 1.5, strokeDasharray: '6 4' },
    });
  }

  return { nodes, edges };
}

function LayoutFitter({ layoutId }: { layoutId: number }) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    if (layoutId === 0) return;
    const timer = setTimeout(() => fitView({ padding: 0.15, duration: 250 }), 60);
    return () => clearTimeout(timer);
  }, [layoutId, fitView]);
  return null;
}

function ProvenanceTabInner({ campaignId }: Props) {
  const stateQuery = useCampaignStateQuery(campaignId);
  const state: CampaignState | undefined = stateQuery.data;
  const [forks, setForks] = useState<ForkEdgeInfo[]>([]);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [layoutId, setLayoutId] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void tryFetchCampaignForks(campaignId).then((f) => {
      if (!cancelled) setForks(f);
    });
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  const graph = useMemo(() => {
    if (!state) return { nodes: [], edges: [] };
    return buildGraph(state, forks);
  }, [state, forks]);

  useEffect(() => {
    if (graph.nodes.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }
    let cancelled = false;
    void layoutProvenance(graph.nodes, graph.edges).then((laid) => {
      if (cancelled) return;
      setNodes(laid.nodes);
      setEdges(laid.edges);
      setLayoutId((id) => id + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [graph, setNodes, setEdges]);

  const t = fallbackTheme;

  if (stateQuery.isLoading) {
    return (
      <div style={{ padding: 16, color: t.textMuted, background: t.background, height: '100%' }}>
        Loading provenance…
      </div>
    );
  }
  if (stateQuery.isError || !state) {
    return (
      <div style={{ padding: 16, color: t.status.failed, background: t.background, height: '100%' }}>
        Failed to load campaign state.
      </div>
    );
  }

  return (
    <div
      data-testid="provenance-dag"
      style={{ width: '100%', height: '100%', background: t.background }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={NODE_TYPES}
        proOptions={PRO_OPTIONS}
        colorMode="dark"
        fitView
      >
        <Background color={t.border} gap={16} />
        <Controls showInteractive={false} />
        <MiniMap
          pannable
          zoomable
          maskColor="rgba(0,0,0,0.5)"
          nodeColor={(n) => {
            if (n.type === 'campaign') return t.accent;
            if (n.type === 'batch') return t.borderStrong;
            if (n.type === 'surrogate') return t.accent;
            if (n.type === 'fork') return t.forkEdge;
            const pt = (n.data as { point?: SamplePoint } | undefined)?.point;
            return pt ? t.status[pt.status] : t.borderStrong;
          }}
        />
        <LayoutFitter layoutId={layoutId} />
      </ReactFlow>
    </div>
  );
}

export function ProvenanceTab(props: Props) {
  // Each mount gets its own ReactFlow context so this tab can be used
  // independently of any outer provider.
  return (
    <ReactFlowProvider>
      <ProvenanceTabInner {...props} />
    </ReactFlowProvider>
  );
}
